// Login screen — Microsoft 365 SSO via Supabase's Azure OIDC provider.
//
// Boot path:
//   1. AuthGate (defined here, used in app.jsx) inspects localStorage for a
//      saved Supabase session. If present + not expired → render <App>.
//   2. If the URL has a `#access_token=…` fragment, that's the OAuth
//      callback — save tokens to localStorage, swap the hash for the route
//      (and MCP consent id) the user started sign-in from
//      (auth_return_route.jsx), render <App>.
//   3. Otherwise render <Login>.
//
// Sign-in flow:
//   - User clicks "Sign in with Microsoft".
//   - We send them to <SUPABASE_URL>/auth/v1/authorize?provider=azure with
//     minimal identity scopes (openid email profile User.Read).
//   - Supabase redirects to Microsoft; Microsoft signs them in (using their
//     existing M365 session if any) and redirects back to Supabase's
//     /auth/v1/callback. Supabase exchanges the code, mints a Supabase JWT,
//     and bounces back to this SPA with `#access_token=…&refresh_token=…`
//     in the URL hash — same shape as the legacy magic-link callback.
//   - Mail.Read / Calendars.Read are *not* requested here. They're asked
//     for incrementally when the user clicks Connect on the Outlook
//     integration card (see /api/integrations/microsoft/oauth/start). This
//     keeps the sign-in consent screen minimal.
//
// Tokens live in localStorage under `atlas.supabaseSession` as a JSON blob
// of { access_token, refresh_token, expires_at }. The api.jsx fetch wrapper
// reads from there for every request — it doesn't care which IdP minted
// the token, so swapping magic-link for OAuth needed no backend or
// api.jsx changes.

const SESSION_KEY = "atlas.supabaseSession";
// JWT `sub` that last came back as a roster member from /api/auth/access —
// lets AccessGate skip the probe on every boot for people already in.
const ACCESS_OK_KEY = "atlas.accessOk";
const SUPABASE_URL = (typeof window !== "undefined" && window.__SUPABASE_URL__) || "";
const SUPABASE_ANON = (typeof window !== "undefined" && window.__SUPABASE_ANON_KEY__) || "";

// Local-dev convenience: when index.html reports a local environment we skip
// the magic-link screen entirely and let api.jsx fall back to the local
// bearer token (the backend only honors it when APP_ENV=local). Append
// ?login=1 to force the real login screen locally — handy for exercising the
// actual Supabase magic-link flow against a configured project.
const LOCAL_ENV = (typeof window !== "undefined" && window.__ATLAS_ENV__) === "local";
const FORCE_LOGIN = (() => {
  try {
    return new URLSearchParams(window.location.search).get("login") === "1";
  } catch (_) {
    return false;
  }
})();

const loadSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s.access_token || !s.expires_at) return null;
    if (s.expires_at * 1000 <= Date.now()) return null; // expired
    return s;
  } catch (_) {
    return null;
  }
};

const saveSession = (s) => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(s));
};

const clearSession = () => {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(ACCESS_OK_KEY);
};

// Read the stored refresh_token directly, regardless of access-token
// expiry. loadSession() returns null once the access token expires (~1h),
// but the refresh token stays valid for ~1 week — so anything that needs
// to *redeem* the refresh token must read it from here, not via
// loadSession(), or an idle user can never be refreshed back in.
const storedRefreshToken = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return "";
    const s = JSON.parse(raw);
    return (s && s.refresh_token) || "";
  } catch (_) {
    return "";
  }
};

// Pull tokens out of the URL hash if this is a magic-link landing. Returns
// the parsed session object or null. Always clears the hash on success so
// the access_token isn't sitting in the address bar.
const consumeHashSession = () => {
  if (typeof window === "undefined" || !window.location.hash) return null;
  const session = sessionFromParams(new URLSearchParams(window.location.hash.slice(1)));
  if (!session) return null;
  saveSession(session);
  // Strip the hash so we don't leak the token on copy/paste of the URL — and
  // put back what a signed-out link carried (stashed before the OAuth
  // redirect): its route, so the user lands on the record instead of Home,
  // and an MCP consent's ?authorization_id=, which app.jsx reads at render.
  const back = (window.AuthReturnRoute && window.AuthReturnRoute.take()) || { search: "", hash: "" };
  history.replaceState(null, "", window.location.pathname + (back.search || window.location.search) + back.hash);
  // App parses the hash when it mounts; the event is for the router
  // listeners already registered. One task later so nothing fires mid-render
  // (this runs inside AuthGate's useState initializer).
  if (back.hash) {
    setTimeout(() => {
      try { window.dispatchEvent(new HashChangeEvent("hashchange")); } catch (_) {}
    }, 0);
  }
  return session;
};

// The callback's token params → our stored session shape. Shared by the web
// hash landing above and the iOS shell's sign-in sheet, so both produce the
// exact same blob.
function sessionFromParams(params) {
  const access = params.get("access_token");
  if (!access) return null;
  const expiresIn = parseInt(params.get("expires_in") || "0", 10);
  return {
    access_token: access,
    refresh_token: params.get("refresh_token") || "",
    expires_at: Math.floor(Date.now() / 1000) + (expiresIn || 3600),
  };
}

// iOS shell sign-in. The Supabase authorize page runs in
// ASWebAuthenticationSession (the AtlasAuth plugin in atlas-mobile/), which
// shares Safari's Microsoft session and catches the custom-scheme redirect
// itself. Must be allow-listed: Supabase → Authentication → URL Configuration
// → Redirect URLs (see atlas-mobile/README.md).
const NATIVE_AUTH_SCHEME = "com.industrypro.atlas";
const NATIVE_AUTH_CALLBACK = NATIVE_AUTH_SCHEME + "://auth-callback";
const nativeAuthPlugin = () =>
  (window.AtlasNative && window.AtlasNative.isNative && window.AtlasNative.plugin("AtlasAuth")) || null;

// Parse the callback URL the sheet hands back. Supabase puts tokens (or
// error/error_description) in the fragment; accept the query too in case a
// flow ever switches. Returns { session } | { error } | {}.
const parseNativeCallback = (raw) => {
  let u = null;
  try { u = new URL(raw); } catch (_) { return { error: "invalid callback" }; }
  const params = new URLSearchParams(u.hash ? u.hash.slice(1) : "");
  if (!params.get("access_token") && !params.get("error")) {
    new URLSearchParams(u.search).forEach((v, k) => params.set(k, v));
  }
  const err = params.get("error_description") || params.get("error");
  if (err) return { error: err };
  const session = sessionFromParams(params);
  return session ? { session } : {};
};

// Use the refresh_token to mint a new access_token. Supabase refresh
// tokens are good for ~1 week by default (configurable per project),
// so as long as the user opens the app at least weekly they stay
// logged in without re-doing the magic-link dance.
//
// Returns the new session on success, null on failure. Concurrent
// callers all await the same in-flight refresh — we don't want 50
// parallel API requests each triggering its own refresh.
let _refreshPromise = null;
const refreshSession = async () => {
  if (_refreshPromise) return _refreshPromise;
  // Read the refresh token straight from storage — NOT via loadSession(),
  // which drops the whole session once the access token has expired (the
  // exact case we're trying to recover from).
  const refreshToken = storedRefreshToken();
  if (!refreshToken) return null;
  if (!SUPABASE_URL || !SUPABASE_ANON) return null;

  _refreshPromise = (async () => {
    try {
      const r = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!r.ok) {
        const bodyText = await r.text().catch(() => "");
        // Only a definitive rejection means the refresh token is truly
        // dead (bad / expired / already-consumed) — Supabase answers those
        // with 400/401. A 5xx, 429, or CORS/network hiccup (common on
        // wake-from-idle while wifi/VPN reconnects) is transient: DON'T
        // clear the session. Return null and let the api.jsx 3-strike
        // guard decide, so a passing blip doesn't sign the user out.
        const definitive = r.status === 400 || r.status === 401;
        try {
          console.warn(
            `Atlas: refresh failed ${r.status}${definitive ? " (clearing session)" : " (transient — keeping session)"}`,
            bodyText
          );
        } catch (_) {}
        if (definitive) clearSession();
        return null;
      }
      const data = await r.json();
      // Malformed 200 (no token) — treat as transient, don't nuke the session.
      if (!data.access_token) {
        try {
          console.warn("Atlas: refresh returned 200 with no access_token — keeping session");
        } catch (_) {}
        return null;
      }
      const next = {
        access_token: data.access_token,
        refresh_token: data.refresh_token || refreshToken,
        expires_at: Math.floor(Date.now() / 1000) + (data.expires_in || 3600),
      };
      saveSession(next);
      return next;
    } catch (err) {
      // Network-level failure (fetch rejects with TypeError) — transient.
      try {
        console.warn("Atlas: refresh error (transient — keeping session)", err);
      } catch (_) {}
      return null;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
};

// Return the current session, transparently refreshing if the access
// token has less than `skewSec` seconds left. Async — call sites that
// can't await should fall back to getSession() (raw read).
const getValidSession = async (skewSec = 60) => {
  const s = loadSession();
  if (s) {
    const remaining = s.expires_at - Math.floor(Date.now() / 1000);
    if (remaining > skewSec) return s;
  }
  // Either within `skewSec` of expiry, or already expired (loadSession
  // returned null). In both cases we may still hold a valid refresh token —
  // redeem it. This is what keeps an idle user signed in: the access token
  // is long gone, but the refresh token isn't.
  const refreshed = await refreshSession();
  return refreshed || null;
};

// Expose to api.jsx and the rest of the SPA.
window.AtlasAuth = {
  getSession: loadSession,
  getValidSession,
  refreshSession,
  clear: clearSession,
  SESSION_KEY,
};

// The official Microsoft "Sign in" mark — required layout per Microsoft's
// brand guidelines (four colored squares, specific colors). Inlined so we
// don't ship another asset just for the login screen.
const MicrosoftLogo = () => (
  <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true" style={{ flexShrink: 0 }}>
    <rect x="1" y="1" width="10" height="10" fill="#F25022" />
    <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
    <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
    <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
  </svg>
);

const Login = ({ onSignedIn }) => {
  const [phase, setPhase] = React.useState("idle"); // 'idle' | 'redirecting' | 'error'
  const [errorMsg, setErrorMsg] = React.useState("");

  // Supabase reports OAuth failures back to us via the URL hash (same
  // channel it uses for success). Surface those in the UI instead of
  // leaving the user staring at a silent login page.
  //
  // Success arrives as `#access_token=…` and is consumed by
  // consumeHashSession() *before* this component ever renders, so we only
  // see the hash here when something went wrong.
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.location.hash) return;
    const params = new URLSearchParams(window.location.hash.slice(1));
    const err = params.get("error_description") || params.get("error");
    if (err) {
      try {
        console.warn("Atlas login: Supabase returned error", err);
      } catch (_) {}
      setErrorMsg(friendlyAuthError(err));
      setPhase("error");
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, []);

  const signInWithMicrosoft = () => {
    if (!SUPABASE_URL || !SUPABASE_ANON) {
      setErrorMsg("Sign-in is not configured on this build. Contact your admin.");
      setPhase("error");
      return;
    }
    setPhase("redirecting");
    const authPlugin = nativeAuthPlugin();
    const params = new URLSearchParams({
      provider: "azure",
      // Where Supabase bounces the user after the OAuth round-trip
      // completes. Must be allow-listed under Supabase dashboard →
      // Authentication → URL Configuration → Redirect URLs.
      redirect_to: authPlugin ? NATIVE_AUTH_CALLBACK : window.location.origin + window.location.pathname,
      // Minimal identity scopes. Mail.Read / Calendars.Read /
      // offline_access live on the same Entra app but are requested
      // *separately* when the user clicks Connect on the Outlook
      // integration card — keeping this consent screen as light as
      // possible improves the new-user experience.
      scopes: "openid email profile User.Read",
    });
    const authorizeUrl = `${SUPABASE_URL}/auth/v1/authorize?${params}`;
    if (authPlugin) {
      signInNative(authPlugin, authorizeUrl);
      return;
    }
    // redirect_to is origin + pathname: the route hash (where Supabase
    // returns its tokens) and ?authorization_id= don't survive the trip, so
    // keep them for consumeHashSession to put back.
    if (window.AuthReturnRoute) window.AuthReturnRoute.save(window.location);
    // Full-page navigation, not fetch — the OAuth dance needs to own the
    // browser tab so it can cookie-set, set headers, etc.
    window.location.assign(authorizeUrl);
  };

  // iOS shell: same authorize URL, run in the system sign-in sheet. The
  // tokens come back as the sheet's callback URL and go through the same
  // session shape as the web hash landing; AuthGate saves it on adoption.
  const signInNative = async (authPlugin, authorizeUrl) => {
    let result;
    try {
      result = await authPlugin.start({ url: authorizeUrl, callbackScheme: NATIVE_AUTH_SCHEME });
    } catch (e) {
      // Closing the sheet is a choice, not a failure — back to idle quietly.
      const code = (e && e.code) || "";
      const msg = String((e && e.message) || e || "");
      if (code === "CANCELLED" || /cancel/i.test(msg)) {
        setPhase("idle");
        return;
      }
      try { console.warn("Atlas login: native sign-in failed", e); } catch (_) {}
      setErrorMsg(friendlyAuthError(msg));
      setPhase("error");
      return;
    }
    const { session, error } = parseNativeCallback(result && result.url);
    if (session) {
      saveSession(session);
      if (window.AtlasNative && window.AtlasNative.haptic) window.AtlasNative.haptic("success");
      onSignedIn(session);
      return;
    }
    try { console.warn("Atlas login: native callback had no session", error || ""); } catch (_) {}
    setErrorMsg(friendlyAuthError(error));
    setPhase("error");
  };

  // Translate raw OAuth errors into copy a non-engineer can act on. We
  // still log the raw value to the console for an admin to grep.
  function friendlyAuthError(raw) {
    const s = String(raw || "").toLowerCase();
    if (s.includes("access_denied") || s.includes("cancel")) {
      return "Microsoft sign-in was cancelled. Try again, or contact an admin if you can't get in.";
    }
    if (s.includes("aadsts50020") || (s.includes("tenant") && s.includes("not"))) {
      return "Your Microsoft account isn't part of IndustryPro. Sign in with your @industrypro.com account.";
    }
    if (
      s.includes("provider is not enabled") ||
      s.includes("not configured") ||
      s.includes("unsupported_provider")
    ) {
      return "Microsoft sign-in isn't enabled on this Atlas yet. Contact your admin to finish setup.";
    }
    if (s.includes("industrypro microsoft account")) {
      return "Atlas is for IndustryPro staff. Sign in with your @industrypro.com Microsoft account.";
    }
    if (s.includes("signup") || s.includes("user not allowed")) {
      return "This Microsoft account isn't on the Atlas roster. Ask an admin to add you.";
    }
    return `Sign-in failed. Contact an admin if this keeps happening.`;
  }

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        display: "flex",
        justifyContent: "center",
        background: "var(--bg, #fafbfc)",
        fontFamily: '"Inter", system-ui, sans-serif',
        // Phone: clear the notch / home indicator (the tablet #root already
        // pads itself; desktop is untouched).
        ...(window.AtlasDevice && window.AtlasDevice.phone
          ? { boxSizing: "border-box", padding: "var(--sat) var(--sar) var(--sab) var(--sal)" }
          : null),
      }}
    >
      <div
        style={{
          background: "var(--surface, #fff)",
          border: "1px solid var(--border, #e5e7eb)",
          borderRadius: 12,
          padding: "32px 36px",
          width: 420,
          maxWidth: "calc(100vw - 32px)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
          /* margin:auto (not align-items:center) so the card centers but stays
             fully reachable by scroll when it's taller than the viewport */
          margin: "auto 16px",
        }}
      >
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <img
              src="/android-chrome-512x512.png"
              alt="IndustryPro"
              width={42}
              height={42}
              style={{ display: "block", flexShrink: 0 }}
            />
            <span
              style={{
                fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
                fontStyle: "italic",
                fontWeight: 600,
                fontSize: 33,
                lineHeight: 1,
                color: "var(--text, #111)",
                letterSpacing: "0.005em",
              }}
            >
              Atlas
            </span>
          </div>
          <div style={{ fontSize: 14, color: "var(--text-2, #666)", marginTop: 14 }}>
            Sign in with your IndustryPro Microsoft 365 account.
          </div>
        </div>

        <button
          type="button"
          onClick={signInWithMicrosoft}
          disabled={phase === "redirecting"}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            width: "100%",
            padding: "11px 12px",
            fontSize: 14,
            fontWeight: 600,
            color: "var(--text, #111)",
            background: "var(--surface, #fff)",
            border: "1px solid var(--border-strong, #d1d5db)",
            borderRadius: 8,
            cursor: phase === "redirecting" ? "wait" : "pointer",
            opacity: phase === "redirecting" ? 0.7 : 1,
            transition: "background 120ms ease",
          }}
          onMouseEnter={(e) => {
            if (phase !== "redirecting")
              e.currentTarget.style.background = "var(--surface-hover, #f3f4f6)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "var(--surface, #fff)";
          }}
        >
          <MicrosoftLogo />
          <span>
            {phase === "redirecting" ? "Redirecting to Microsoft…" : "Sign in with Microsoft"}
          </span>
        </button>

        {phase === "error" && (
          <div
            style={{
              marginTop: 14,
              padding: "10px 12px",
              fontSize: 12.5,
              lineHeight: 1.5,
              color: "var(--rose-fg, #b91c1c)",
              background: "var(--rose-bg, #fef2f2)",
              border: "1px solid var(--rose-bg, #fee2e2)",
              borderRadius: 6,
            }}
          >
            {errorMsg}
          </div>
        )}

        <div style={{ fontSize: 11, color: "var(--text-3, #888)", marginTop: 16, lineHeight: 1.5 }}>
          Atlas uses your IndustryPro M365 sign-in — no separate password. If your account is
          disabled in Microsoft, your Atlas access ends immediately too. New here? Sign in and
          you'll be asked a few questions to request access.
        </div>
      </div>
    </div>
  );
};

// Minimal full-screen "restoring your session" state, shown for the brief
// moment while AuthGate redeems a refresh token on boot. Matches the login
// screen's background so there's no flash between it and <App>/<Login>.
const AuthRestoring = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "var(--bg, #fafbfc)",
      fontFamily: '"Inter", system-ui, sans-serif',
      color: "var(--text-3, #888)",
      fontSize: 13,
    }}
  >
    Restoring your session…
  </div>
);


// ── Access requests ──────────────────────────────────────────────────────
// A Microsoft sign-in from IndustryPro that has no roster row lands here
// instead of a broken app: a short request (position, team, manager) that
// DMs the Atlas approver in Slack, then a waiting screen that polls until
// they're approved. See backend app/access_requests.py.

const jwtSub = (token) => {
  try {
    const part = String(token || "").split(".")[1] || "";
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json).sub || null;
  } catch (_) {
    return null;
  }
};

const accessFetch = async (session, path, init = {}) => {
  const base = window.AtlasApi?.getBaseUrl?.() || "";
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body };
};

const AccessCard = ({ title, children }) => (
  <div
    style={{
      height: "100%",
      overflowY: "auto",
      display: "flex",
      justifyContent: "center",
      background: "var(--bg, #fafbfc)",
      fontFamily: '"Inter", system-ui, sans-serif',
    }}
  >
    <div
      style={{
        background: "var(--surface, #fff)",
        border: "1px solid var(--border, #e5e7eb)",
        borderRadius: 12,
        padding: "28px 32px",
        width: 440,
        maxWidth: "calc(100vw - 32px)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
        margin: "auto 16px",
      }}
    >
      <div style={{ fontSize: 17, fontWeight: 600, color: "var(--text, #111)", marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  </div>
);

const accessInput = {
  width: "100%",
  boxSizing: "border-box",
  padding: "8px 10px",
  fontSize: 13,
  color: "var(--text, #111)",
  background: "var(--surface, #fff)",
  border: "1px solid var(--border-strong, #d1d5db)",
  borderRadius: 6,
  fontFamily: "inherit",
};

const AccessField = ({ label, children }) => (
  <label style={{ display: "block", marginTop: 12 }}>
    <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-2, #555)", marginBottom: 4 }}>{label}</div>
    {children}
  </label>
);

const accessButton = (primary) => ({
  padding: "9px 14px",
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 7,
  cursor: "pointer",
  border: primary ? "1px solid var(--accent, #2563eb)" : "1px solid var(--border-strong, #d1d5db)",
  background: primary ? "var(--accent, #2563eb)" : "var(--surface, #fff)",
  color: primary ? "#fff" : "var(--text, #111)",
});

const accessMuted = { fontSize: 13, lineHeight: 1.55, color: "var(--text-2, #555)" };

const AccessScreen = ({ session, data, setData, onSignOut }) => {
  const OTHER = "__other__";
  const [form, setForm] = React.useState(() => ({
    full_name: data.full_name || "",
    position: "",
    position_text: "",
    team: "",
    team_text: "",
    reports_to: "",
    note: "",
  }));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Waiting for approval: re-check every 20s; approval reloads into the app.
  React.useEffect(() => {
    if (data.status !== "pending") return undefined;
    const t = setInterval(async () => {
      const r = await accessFetch(session, "/api/auth/access").catch(() => null);
      if (r && r.ok && r.body.status !== "pending") setData(r.body);
    }, 20000);
    return () => clearInterval(t);
  }, [data.status]);

  const submit = async () => {
    const position = form.position === OTHER || form.position === "Other" ? form.position_text : form.position;
    if (!form.full_name.trim()) return setError("Add your name.");
    if (!(position || "").trim()) return setError("Add your position.");
    setBusy(true);
    setError("");
    const r = await accessFetch(session, "/api/auth/access/request", {
      method: "POST",
      body: JSON.stringify({
        full_name: form.full_name.trim(),
        position: position.trim(),
        team_id: form.team && form.team !== OTHER ? form.team : null,
        team_text: form.team === OTHER ? form.team_text : null,
        reports_to: form.reports_to || null,
        note: form.note || null,
      }),
    }).catch(() => null);
    setBusy(false);
    if (!r || !r.ok) return setError((r && r.body && r.body.detail) || "Couldn't send the request. Try again.");
    setData(r.body);
  };

  const signOut = (
    <button type="button" onClick={onSignOut} style={{ ...accessButton(false), marginTop: 18 }}>
      Sign out
    </button>
  );

  if (data.status === "pending") {
    const req = data.request || {};
    return (
      <AccessCard title="Request sent">
        <div style={accessMuted}>
          Your Atlas access is waiting for approval. You'll get a Slack message when you're in.
        </div>
        <div style={{ ...accessMuted, marginTop: 12, fontSize: 12.5 }}>
          {[req.full_name, req.position, req.team].filter(Boolean).join(" · ")}
        </div>
        {signOut}
      </AccessCard>
    );
  }
  if (data.status !== "none") {
    const copy = {
      denied: ["Access not approved", "Your Atlas access request wasn't approved. Talk to your manager or an Atlas admin."],
      deactivated: ["Access removed", "Your Atlas access was turned off. Talk to an Atlas admin if that's a mistake."],
      not_allowed: ["Use your IndustryPro account", "Atlas is for IndustryPro staff. Sign in with your @industrypro.com Microsoft account."],
    }[data.status] || ["Atlas access", "Something went wrong checking your access. Reload, or talk to an admin."];
    return (
      <AccessCard title={copy[0]}>
        <div style={accessMuted}>{copy[1]}</div>
        {signOut}
      </AccessCard>
    );
  }
  return (
    <AccessCard title="Request Atlas access">
      <div style={accessMuted}>
        You're signed in as {data.email}, but you're not set up in Atlas yet. Tell us where you sit and an admin
        will approve you.
      </div>
      <AccessField label="Your name">
        <input style={accessInput} value={form.full_name} onChange={set("full_name")} />
      </AccessField>
      <AccessField label="Position">
        <window.Picker value={form.position} onChange={(v) => set("position")({ target: { value: v } })}
          width="100%" placeholder="Choose…" options={data.positions || []} />
        {form.position === "Other" && (
          <input style={{ ...accessInput, marginTop: 6 }} placeholder="Your title" value={form.position_text} onChange={set("position_text")} />
        )}
      </AccessField>
      <AccessField label="Team">
        <window.Picker value={form.team} onChange={(v) => set("team")({ target: { value: v } })}
          width="100%" placeholder="Choose…"
          options={[...(data.teams || []).map((t) => ({ value: String(t.id), label: t.name })), { value: OTHER, label: "Other / not sure" }]} />
        {form.team === OTHER && (
          <input style={{ ...accessInput, marginTop: 6 }} placeholder="Which team?" value={form.team_text} onChange={set("team_text")} />
        )}
      </AccessField>
      <AccessField label="Who do you report to?">
        <input style={accessInput} value={form.reports_to} onChange={set("reports_to")} />
      </AccessField>
      <AccessField label="Anything else? (optional)">
        <textarea style={{ ...accessInput, minHeight: 60, resize: "vertical" }} value={form.note} onChange={set("note")} />
      </AccessField>
      {error && <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--rose-fg, #b91c1c)" }}>{error}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
        <button type="button" onClick={submit} disabled={busy} style={accessButton(true)}>
          {busy ? "Sending…" : "Request access"}
        </button>
        <button type="button" onClick={onSignOut} style={accessButton(false)}>Sign out</button>
      </div>
    </AccessCard>
  );
};

// Sits between a live session and <App>: a roster member goes straight
// through (and is remembered, so later boots skip the probe); anyone else
// sees AccessScreen. Any failure of the probe itself falls through to the
// app, which then behaves exactly as before this gate existed.
const AccessGate = ({ session, onSignOut, children }) => {
  const sub = jwtSub(session && session.access_token);
  const known = (() => {
    try { return !!sub && localStorage.getItem(ACCESS_OK_KEY) === sub; } catch (_) { return false; }
  })();
  const [data, setData] = React.useState(known ? { status: "member" } : null);

  React.useEffect(() => {
    if (known) return undefined;
    let cancelled = false;
    accessFetch(session, "/api/auth/access")
      .then((r) => { if (!cancelled) setData(r.ok ? r.body : { status: "member" }); })
      .catch(() => { if (!cancelled) setData({ status: "member" }); });
    return () => { cancelled = true; };
  }, [sub]);

  React.useEffect(() => {
    if (data && data.status === "member" && sub) {
      try { localStorage.setItem(ACCESS_OK_KEY, sub); } catch (_) {}
    }
  }, [data && data.status, sub]);

  if (!data) return <AuthRestoring />;
  if (data.status === "member") return children;
  return <AccessScreen session={session} data={data} setData={setData} onSignOut={onSignOut} />;
};

// AuthGate decides whether to show the login screen or the main app shell.
// On mount it:
//   1. Tries to consume tokens from the URL hash (magic-link callback).
//   2. Otherwise loads any saved (still-live) session from localStorage.
//   3. If neither, but a refresh token is still on disk, redeems it before
//      giving up — otherwise a reload after the ~1h access-token lifetime
//      would bounce the user to login and discard a week-valid refresh
//      token. Shows <AuthRestoring> during that one-shot refresh.
//   4. Sets a state flag that re-renders into <App /> when authenticated.
const AuthGate = ({ children }) => {
  const [session, setSessionState] = React.useState(() => consumeHashSession() || loadSession());

  // No live session on boot, but a refresh token exists → try one refresh
  // before rendering <Login>. `booting` gates the render so we don't flash
  // the login screen and then swap to the app a moment later.
  const [booting, setBooting] = React.useState(() => !session && !!storedRefreshToken());

  React.useEffect(() => {
    if (!booting) return undefined;
    let cancelled = false;
    (async () => {
      const refreshed = await refreshSession().catch(() => null);
      if (cancelled) return;
      if (refreshed?.access_token) setSessionState(refreshed);
      setBooting(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []); // one-shot on mount

  // Keep window-level session in sync so the api.jsx fetch wrapper can read
  // a fresh value without a React re-render every time.
  //
  // Only clear storage when the session goes null AFTER having been set
  // (an explicit sign-out or a definitively-dead token). On the initial
  // null — the reload-with-expired-access-token case — we must leave the
  // stored refresh token alone so the boot refresh above can redeem it;
  // clearing here would wipe it before we ever get the chance, and would
  // also destroy a still-valid token on a transient boot-refresh failure.
  const hadSessionRef = React.useRef(!!session);
  React.useEffect(() => {
    if (session) {
      hadSessionRef.current = true;
      saveSession(session);
    } else if (hadSessionRef.current) {
      hadSessionRef.current = false;
      clearSession();
    }
  }, [session]);

  // Cross-tab sync via the `storage` event. Solves three real problems:
  //
  //   1. Magic-link tab handoff. Email clients open the magic-link URL in
  //      a new browser tab. Without this listener, the user ends up with
  //      two tabs — the original (stuck on "check your email") and the
  //      new one (authenticated). With it, the original tab notices the
  //      session arriving and renders <App> immediately.
  //
  //   2. Multi-tab logout. Signing out in one tab signs out everywhere.
  //
  //   3. Refresh-token race. Supabase rotates refresh_tokens on use, so
  //      two tabs trying to refresh in parallel would have one fail
  //      ("refresh_token already consumed") and bounce its user to
  //      login. Now, when tab A writes the new session after refreshing,
  //      tab B adopts it instead of trying its own refresh.
  //
  // The `storage` event only fires in tabs OTHER than the one that wrote
  // the value, so there's no infinite-loop concern.
  React.useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== SESSION_KEY) return;
      if (e.newValue) {
        try {
          const next = JSON.parse(e.newValue);
          if (next?.access_token) setSessionState(next);
        } catch (_) {
          /* corrupted entry — ignore */
        }
      } else {
        // Other tab cleared the session (signed out). Mirror that.
        setSessionState(null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (booting) return <AuthRestoring />;
  if (!session && (!LOCAL_ENV || FORCE_LOGIN)) {
    return <Login onSignedIn={setSessionState} />;
  }
  if (session) {
    return (
      <AccessGate session={session} onSignOut={() => setSessionState(null)}>
        {children}
      </AccessGate>
    );
  }
  return children;
};

window.AtlasAuth.AuthGate = AuthGate;
window.AtlasAuth.Login = Login;
